/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { generateFlattenedEntities } from 'src/luis/modules/entities/utils/entityTreeUtils';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';

/**
 * Takes an array of entities and a selection. Returns a flattened list of entities with special tree related data
 * such as nesting level, parent id, node expend/collapse information, etc..
 */
export const useEntityTree = (entityStores: readonly EntityStore[], selectedId?: LuisId) => {
    const [collapsedEntities, setCollapsedEntities] = React.useState<Record<string, boolean>>({});
    const [newChildParentId, setNewChildParentId] = React.useState<LuisId>(null);

    const memoizedItems = React.useMemo(() => generateFlattenedEntities(entityStores, collapsedEntities, newChildParentId), [
        entityStores,
        collapsedEntities,
        newChildParentId
    ]);

    /**
     * Show/hide an entity's children in the tree
     */
    const toggleCollapse = (nodeId: string) => {
        const collapsedEntitiesNew = { ...collapsedEntities, [nodeId]: !collapsedEntities[nodeId] };
        setCollapsedEntities(collapsedEntitiesNew);
    };

    const entityItems = React.useMemo(() => memoizedItems.map(item => ({ ...item, isSelected: item.entityStore.id === selectedId })), [
        memoizedItems,
        selectedId
    ]);

    return { entityItems, setNewChildParentId, toggleCollapse };
};
